import type { BoxProps } from '@chakra-ui/react'
import { Badge, Box, Button, Heading } from '@chakra-ui/react'
import type { AppInfoMessage } from 'BrokerTsLib/src/dtos'
import { Link } from 'react-router'

import type { CardProps } from '../../components/Card'
import { Card, CardBody } from '../../components/Card'
import { Loading } from '../../components/Loading'
import { Pagination } from '../../components/Pagination'
import { useAuth } from '../../context/AuthContext'
import { getNewsItemTypeColor } from '../../data/news'
import { useApp } from '../../hooks/useApp'
import { ArrowLeft, Calendar, Clock, Settings, User } from '../../icons'
import { formatDate } from '../../utils/formatDate'
import { NewsGrid } from './NewsGrid'
import { NewsItemTypeBadge } from './NewsItem'

interface NewsDetailProps extends AppInfoMessage {
  otherNews: Array<AppInfoMessage>,
  totalCount: number,
  currentPage: number,
  pageSize: number,
  setCurrentPage: any,
  isLoadingMessages: boolean
}

type NewsMetadataIconType = 'user' | 'calendar' | 'clock'

interface NewsMetadataItemProps {
  icon?: NewsMetadataIconType
}

interface NewsMetadataItemProps extends BoxProps {
  icon?: NewsMetadataIconType
  colorScheme?: string
}

const NewsMetadataIcon = ({ icon, colorScheme }: { icon: NewsMetadataIconType, colorScheme?: string }) => {
  const color = 'secondary.500'
  switch (icon) {
    case 'user':
      return <User color={color} />
    case 'calendar':
      return <Calendar color={color} />
    case 'clock':
      return <Clock color={color} />
  }
}

const NewsDetailWrapper = (props: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'start',
        gap: 16,
      }}
      {...props}
    />
  )
}

const NewsDetailArticle = (props: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'grid',
        justifyItems: 'start',
        columnGap: 16,
        rowGap: 16,
        gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
        w: '100%'
      }}
      {...props}
    />
  )
}

const NewsDetailContent = (props: BoxProps) => {
  return <Box sx={{ display: 'grid', placeContent: 'start', gap: 4 }} {...props} />
}

const NewsDetailMetadata = (props: CardProps) => {
  const { children, as, sx, colorScheme, ...rest } = props

  return (
    <Card as={as} sx={{ borderLeft: '2px solid', borderColor: `${colorScheme}.500`, ...sx }} {...rest}>
      <CardBody sx={{ display: 'grid', p: 4, gap: 4, placeItems: 'start' }}>
        {props.children}
      </CardBody>
    </Card>
  )
}

const NewsMetadataItem = ({ icon, colorScheme, ...rest }: NewsMetadataItemProps) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {icon && <NewsMetadataIcon icon={icon} colorScheme={colorScheme} />}
      {rest.children}
    </Box>
  )
}

const NewsOtherNewsWrapper = (props: BoxProps) => {
  return <Box sx={{ display: 'grid', gap: 4 }} {...props} />
}

export const NewsDetail = ({
  id,
  //author,
  messageHeader: header,
  messageDate: date,
  messageImportance: importance,
  messageText: text,
  otherNews,
  type,
  messageShow: show,
  totalCount,
  currentPage,
  setCurrentPage,
  pageSize,
  isLoadingMessages
}: NewsDetailProps) => {
  const colorScheme = getNewsItemTypeColor(importance)

  const { app } = useApp()
  const { user } = useAuth()!
  const newsLink = `/${app}/news`
  const editLink = `/news/new/${id}`
  const newsAdminLink = '/news/admin'

  return (
    <NewsDetailWrapper>
      <NewsDetailArticle as="article">
        <NewsDetailContent as="main">
          <Heading as="h1" sx={{ fontSize: '3xl' }}>
            {header}
          </Heading>

          <Box dangerouslySetInnerHTML={{ __html: text ?? '' }}></Box>
        </NewsDetailContent>

        <NewsDetailMetadata sx={{ w: '100%' }} as="aside" colorScheme={colorScheme}>
          {type && <Badge colorScheme='blue'>{type}</Badge>}
          {date && <NewsMetadataItem icon="calendar" colorScheme={colorScheme}>{formatDate({ date })}</NewsMetadataItem>}
          {date && (
            <NewsMetadataItem icon="user" colorScheme={colorScheme}>
              Priorita: <NewsItemTypeBadge priority={importance} />
            </NewsMetadataItem>
          )}

          {(user?.allowEditAppInfoMessages && !!show) && <Badge colorScheme="blue">Zobrazená</Badge>}
          {(user?.allowEditAppInfoMessages && !show) && <Badge colorScheme="gray">Skrytá</Badge>}

          {user?.allowEditAppInfoMessages &&
            <Link to={editLink}>
              <Button
                variant='outline'
                size='sm'
              >
                Editovat
              </Button>
            </Link>
          }
        </NewsDetailMetadata>

        <Button as={Link} to={newsLink} variant="ghost">
          <ArrowLeft color="secondary.500" />
          Přejít na aktuality
        </Button>

        {user?.allowEditAppInfoMessages &&
          <Button as={Link} to={newsAdminLink} variant="ghost" sx={{ marginLeft: 'auto' }}>
            <Settings color="secondary.500" />
            Přejít na správu aktualit
          </Button>
        }
      </NewsDetailArticle>

      <NewsOtherNewsWrapper>
        <Heading as="h2" sx={{ fontSize: 'lg' }}>
          Další aktuality
        </Heading>

        {!isLoadingMessages && (
          <>
            <NewsGrid news={otherNews} />
            <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={7} pageSize={pageSize} />
          </>
        )}
        {isLoadingMessages && <Loading large={true} />}
      </NewsOtherNewsWrapper>
    </NewsDetailWrapper>
  )
}
